import { sortBy } from 'lodash'
import { useEffect } from 'react'
import styled from 'styled-components'
// components
import { Avatar } from '@outbound-ai/ursa'
import { generateHSLString } from 'utils/helper'
import { CallChatMessage } from './CallChatMessage'
// assets
import CallChatPlanAvatar from 'assets/images/icons/CallChatPlanAvatar.svg?react'
import HCAAvatar from 'assets/images/icons/HCAAvatar.svg?react'
import VaAvatar from 'assets/images/icons/VaAvatar.svg?react'
// types
import {
  CallParticipantTypeEnum,
  callParticipantTypesOurUser,
  CallTypeToJobTypeEnum,
  ICall,
  ITranscriptMessage,
} from 'features/calls/types'
import { IClaim } from 'features/claims/types'
// utils
import { useAppSelector } from 'appState/store'
import { dateDiffToHHMMSS } from 'utils/date'
import { formatPhoneNumber } from 'utils'
import { scrollElementToBottom } from 'utils/dom'
import { isOutboundUser } from 'utils/Utils'

interface IProps {
  transcript: ITranscriptMessage[]
  claim: IClaim
  call: ICall
  callStartTime: string
  collapseLongMessages?: boolean
}

export const CallChatDisplay = ({ transcript, claim, call, callStartTime, collapseLongMessages = false }: IProps) => {
  const { user } = useAppSelector((state) => state.auth)
  const isManualCall = call.jobType === CallTypeToJobTypeEnum.standard
  const payerDisplayName = isManualCall ? formatPhoneNumber(call.phoneNumber) : call.preferredPayerName ? call.preferredPayerName : claim.insurerName

  // effects
  useEffect(() => {
    scrollElementToBottom('chat-display-transcript')
  }, [transcript])

  // variables
  const userTitle =
    call.jobType === CallTypeToJobTypeEnum.diagnosticHeadset
      ? `${user?.given_name} ${user?.family_name}`
      : `${call.initiatorUser?.firstName} ${call.initiatorUser?.lastName}`
  const participantTypeToTitleMapping = {
    // TAKEOVER: At present the transcripts are faked to show twilioParticipant and browserParticipant like they used to but it might be wise to know the difference
    [CallParticipantTypeEnum.foreignPhoneParticipant]: payerDisplayName,
    [CallParticipantTypeEnum.twilioParticipant]: payerDisplayName,
    [CallParticipantTypeEnum.domesticPhoneParticipant]: userTitle,
    [CallParticipantTypeEnum.browserParticipant]: userTitle,
    [CallParticipantTypeEnum.AIAgentParticipant]: 'AI Agent',
    [CallParticipantTypeEnum.HCAgentParticipant]: 'HC Agent',
    [CallParticipantTypeEnum.transcriptEventDetectorParticipant]: 'AI Agent',
    [CallParticipantTypeEnum.diagnosticParticipant]: 'Diagnostic Agent',
    [CallParticipantTypeEnum.jobSchedulerParticipant]: 'Call Scheduler',
    [CallParticipantTypeEnum.DoubleTranscription]: 'Extraction Transcript',
  }

  // Helper function to create Avatar from user/call
  const createUserAvatar = (user?: any, call?: ICall) => {
    const familyName = user?.family_name || call?.initiatorUser?.lastName || ''
    const givenName = user?.given_name || call?.initiatorUser?.firstName || ''
    const hsl = generateHSLString(givenName + familyName)
    const initials =
      familyName && givenName ? givenName?.charAt(0)?.toUpperCase() + '' + familyName?.charAt(0)?.toUpperCase() : 'N/A'
    return (
      <Avatar
        initials={initials}
        size="medium"
        backgroundColor={hsl}
        color="#32325d"
        style={{ cursor: 'pointer', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)' }}
      />
    )
  }

  const participantTypeToIconMapping = {
    [CallParticipantTypeEnum.foreignPhoneParticipant]: <CallChatPlanAvatar />,
    [CallParticipantTypeEnum.twilioParticipant]: <CallChatPlanAvatar />,
    [CallParticipantTypeEnum.domesticPhoneParticipant]: createUserAvatar(
      call.jobType === CallTypeToJobTypeEnum.diagnosticHeadset ? user! : undefined,
      call!,
    ),
    [CallParticipantTypeEnum.browserParticipant]: createUserAvatar(
      call.jobType === CallTypeToJobTypeEnum.diagnosticHeadset ? user! : undefined,
      call!,
    ),
    [CallParticipantTypeEnum.AIAgentParticipant]: <VaAvatar />,
    [CallParticipantTypeEnum.diagnosticParticipant]: <VaAvatar />,
    [CallParticipantTypeEnum.HCAgentParticipant]: <HCAAvatar />,
    [CallParticipantTypeEnum.DoubleTranscription]: <VaAvatar />,
  }

  const eventTypeToMessage: Map<string, string> = new Map<string, string>([
    ['HumanTakeOverFromBrowser', '{0} took over the call at {2}'],
    ['HumanAgentHoldStart', '{0} on hold with {3} at {2}'],
    ['HumanAgentHoldEnd', '{0} heard a {3} agent answer at {2}'],
    ['DialedCallBusy', '{1} phone was busy at {2}'],
    ['DialedCallCompleted', 'Call with {0} ended at {2}'],
    ['DialedCallFailed', '{1} call failed at {2}'],
    ['DialedCallNoAnswer', '{1} call failed unanswered at {2}'],
    ['DialedCallRinging', '{1} phone started ringing at {2}'],
    ['DialedCallInProgress', '{0} answered at {2}'],
    ['ParticipantStoppedConversation', '{0} hung up at {2}'],
    ['TakeOverFromBrowserFailed', 'Failed to take over the call at {2}'],
  ])

  const messages = sortBy(transcript, 'created').map((message) => {
    let messageData = ''
    try {
      if (!isOutboundUser(user!)) {
        if (message.participantType === CallParticipantTypeEnum.DoubleTranscription) {
          return
        }
      }
      messageData = JSON.parse(message.message)
    } catch (e) {
      // message is not json string object , do nothing
    }

    let messageText = message.message
    let eventType = false

    if (typeof messageData === 'object' && messageData['eventType']) {
      const template = eventTypeToMessage.get(messageData['eventType'])
      const youYour =
        callParticipantTypesOurUser.includes(message.participantType) &&
        user?.given_name + ' ' + user?.family_name ===
        call.initiatorUser?.firstName + ' ' + call.initiatorUser?.lastName
      const theyTheir =
        participantTypeToTitleMapping[message.participantType as keyof typeof participantTypeToIconMapping]
      if (template) {
        eventType = true
        const args = [
          youYour ? 'You' : theyTheir,
          youYour ? 'Your' : theyTheir,
          `${dateDiffToHHMMSS(new Date(message.created).toISOString(), new Date(callStartTime).toISOString())}`,
          'payer',
        ]
        messageText = template.replace(/{(\d+)}/g, function (match, number) {
          return typeof args[number] != 'undefined' ? args[number] : match
        })
      }
    }

    return {
      Icon: participantTypeToIconMapping[message.participantType as keyof typeof participantTypeToIconMapping] || (
        <VaAvatar />
      ),
      title: `${participantTypeToTitleMapping[message.participantType as keyof typeof participantTypeToIconMapping] ||
        'Unknown participant'
        } · ${dateDiffToHHMMSS(new Date(message.created).toISOString(), new Date(callStartTime).toISOString(), true)}`,
      message: messageText,
      eventType: eventType,
      id: message.id,
    }
  })
  return (
    <Section id="chat-display-transcript" className="grey-scrollbar">
      {messages.filter(Boolean).map((message) => {
        return <CallChatMessage key={message!.id} {...message!} collapseLongMessages={collapseLongMessages} />
      })}
    </Section>
  )
}

const Section = styled.section`
  width: 100%;
  min-width: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  padding: 1rem 1.5rem 6rem;
  gap: 1rem;
`
